import { useContext, useState } from "react";

import { Trans, useTranslation } from "react-i18next";

import { AxiosError } from "axios";
import moment from "moment";
import { css } from "@emotion/react";

import { BannerType, BannerView } from "@dotnet/telematics-design-system";

import DialogActions from "components/Actions/dialogs/components/DialogActions";
import { OtpInputField } from "components/Auth/shared/OtpInputField";
import { BUTTON_COLORS } from "components/DesignComponents/Button/types/enums";
import Dialog from "components/DesignComponents/Dialog";
import TextLink, {
    TEXT_LINK_SIZES
} from "components/DesignComponents/TextLink";
import AuthContext from "context/auth/authContext";
import TimeZoneContext from "context/timeZone/timeZoneContext";
import { isAuthenticatorType } from "shared/functions";
import { useApi } from "shared/hooks/useApi";
import { useMediaQueries } from "shared/hooks/useMediaQueries";
import { useTranslatedExternalLinks } from "shared/hooks/useTranslatedExternalLinks";
import { maxDialogWidth } from "shared/styles/styles";
import { body2SemiBold } from "shared/styles/typography";
import { ENDPOINTS } from "shared/types/enums";
import { DialogHandler } from "shared/types/interfaces";

const DeleteAccountModal = ({ isOpen, close }: DialogHandler) => {
    const { currentTimeZone } = useContext(TimeZoneContext);
    const { authType, user, logoutUser } = useContext(AuthContext);

    const { t } = useTranslation();
    const { postData, handleResponse } = useApi();
    const { privacyPolicy } = useTranslatedExternalLinks();
    const { fromMd } = useMediaQueries();

    const [isLoading, setIsLoading] = useState(false);
    const [otpCode, setOtpCode] = useState("");
    const [error, setError] = useState<string | null>(null);

    const handleOtpChange = (newValue: string) => {
        setError(null);
        setOtpCode(newValue);
    };

    const handleSubmit = async () => {
        try {
            setIsLoading(true);

            await postData(ENDPOINTS.UsersSelfDelete, {
                otp_code: otpCode,
                two_factor_auth_type: authType
            });

            logoutUser(true, {
                status: BannerType.Info,
                title: t("Flash##account was deleted"),
                description: t("Flash##check your email for details")
            });
        } catch (error) {
            const err = error as AxiosError;

            if (err.response?.status === 400) {
                setError(t("Security##2FA##send otp code##incorrect code"));
            } else {
                handleResponse(error);
            }
        }

        setIsLoading(false);
    };

    const thirtyDaysFromToday = moment
        .tz(currentTimeZone)
        .add(30, "days")
        .format("L");

    const email = user?.email;

    return (
        <Dialog
            open={isOpen}
            close={close}
            isTitleSeparator
            isActionsSeparator
            title={t("Security##delete account##confirmation##title")}
            description={t(
                "Security##delete account##confirmation##description"
            )}
            submit={handleSubmit}
            slotProps={{
                transition: {
                    onExited: () => {
                        setIsLoading(false);
                        setOtpCode("");
                        setError(null);
                    }
                }
            }}
            actions={
                <DialogActions
                    onClose={close}
                    isLoading={isLoading}
                    isReadyToConfirm={otpCode.length === 6}
                    confirmationButtonText={t("Button##delete")}
                    submitBtnColor={BUTTON_COLORS.Critical}
                />
            }
            css={css(fromMd && maxDialogWidth)}
        >
            <BannerView
                bannerType={BannerType.Warning}
                title={t("Security##delete account##confirmation##banner", {
                    date: thirtyDaysFromToday
                })}
            />

            <div
                css={[
                    body2SemiBold,
                    {
                        margin: "16px 0 8px 0",
                        "& > p:not(:last-of-type)": { margin: "0" }
                    }
                ]}
            >
                <p>
                    {t("Security##delete account##confirmation##account")}:{" "}
                    <span
                        css={css({
                            fontWeight: "600",
                            wordBreak: "break-word"
                        })}
                    >
                        {email}
                    </span>
                </p>

                <p>
                    {t("Security##delete account##confirmation##cancel info", {
                        date: thirtyDaysFromToday
                    })}
                </p>

                <p>
                    <Trans
                        i18nKey="Security##delete account##confirmation##more info"
                        components={{
                            a: (
                                <TextLink
                                    size={TEXT_LINK_SIZES.Normal}
                                    target="_blank"
                                    href={privacyPolicy}
                                >
                                    ""
                                </TextLink>
                            )
                        }}
                    />
                </p>

                <p
                    css={css({
                        margin: "24px 0 8px 0"
                    })}
                >
                    {t(
                        `Security##delete account##confirmation##verification code description##${authType?.toLowerCase()}`
                    )}
                </p>

                <OtpInputField
                    isLoading={isLoading}
                    error={error}
                    onChange={handleOtpChange}
                    label={
                        isAuthenticatorType(authType)
                            ? undefined
                            : t(
                                  "Security##2FA##send email code##verification code"
                              )
                    }
                    isRequired
                />
            </div>
        </Dialog>
    );
};

export default DeleteAccountModal;
